import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Copy, Loader2, MessageCircle, Plus, ShieldAlert } from 'lucide-react'
import { API_ROUTES } from '@/config/api-routes'
import { useAuth } from '@/contexts/AuthContext'
import {
  aprovarTemplateLocal, criarTemplate, getCanal, getMensageriaConfig, listTemplates,
  salvarCanal, salvarMensageriaConfig,
  type CanalConexao, type MensageriaConfig, type WhatsTemplate,
} from '@/services/atendimento'

export default function ConfigWhatsAppPage() {
  const { current, papel } = useAuth()
  const souTitular = papel === 'medico_titular'

  const [canal, setCanal] = useState<CanalConexao | null>(null)
  const [cfg, setCfg] = useState<MensageriaConfig>({ whatsapp_ativo: true, envio_automatico: false, teto_envios_hora: 60 })
  const [templates, setTemplates] = useState<WhatsTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [copiado, setCopiado] = useState(false)

  const [provedor, setProvedor] = useState<'simulado' | 'cloud_api'>('simulado')
  const [nome, setNome] = useState('WhatsApp do consultório')
  const [telefone, setTelefone] = useState('')
  const [phoneNumberId, setPhoneNumberId] = useState('')
  const [verifyToken, setVerifyToken] = useState('')

  const [tplNome, setTplNome] = useState('')
  const [tplCorpo, setTplCorpo] = useState('')

  const carregar = useCallback(async () => {
    if (!current) return
    setErro(null)
    try {
      const [c, m, t] = await Promise.all([
        getCanal(current.workspace.id),
        getMensageriaConfig(current.workspace.id),
        listTemplates(current.workspace.id),
      ])
      setCanal(c)
      setCfg(m)
      setTemplates(t)
      if (c) {
        setProvedor(c.provedor); setNome(c.nome)
        setTelefone(c.telefone_exibicao ?? ''); setPhoneNumberId(c.phone_number_id ?? '')
        setVerifyToken(c.verify_token ?? '')
      } else {
        setVerifyToken(crypto.randomUUID().replace(/-/g, ''))
      }
    } catch {
      setErro('Não foi possível carregar. A migração phase8 já foi aplicada?')
    } finally {
      setLoading(false)
    }
  }, [current])

  useEffect(() => { carregar() }, [carregar])

  async function salvarConexao(e: React.FormEvent) {
    e.preventDefault()
    if (!current) return
    setBusy(true); setErro(null); setAviso(null)
    try {
      await salvarCanal({
        workspaceId: current.workspace.id,
        id: canal?.id,
        provedor, nome,
        telefoneExibicao: telefone || null,
        phoneNumberId: provedor === 'cloud_api' ? phoneNumberId : null,
        verifyToken,
      })
      setAviso(provedor === 'simulado'
        ? 'Conexão simulada pronta — já dá para atender pelo módulo Atendimento.'
        : 'Conexão salva. Falta cadastrar o token de acesso (veja as instruções abaixo) e apontar o webhook na Meta.')
      await carregar()
    } catch {
      setErro('Não foi possível salvar (só o médico titular administra a conexão).')
    } finally { setBusy(false) }
  }

  async function salvarConfig(patch: Partial<MensageriaConfig>) {
    if (!current) return
    const novo = { ...cfg, ...patch }
    setCfg(novo)
    try { await salvarMensageriaConfig(current.workspace.id, novo) } catch { setErro('Falha ao salvar a configuração.') }
  }

  async function novoTemplate(e: React.FormEvent) {
    e.preventDefault()
    if (!current || tplNome.trim().length < 3 || tplCorpo.trim().length < 5) return
    setBusy(true)
    try {
      await criarTemplate({ workspaceId: current.workspace.id, canalId: canal?.id ?? null, nome: tplNome, corpo: tplCorpo })
      setTplNome(''); setTplCorpo('')
      await carregar()
    } catch {
      setErro('Não foi possível criar o template.')
    } finally { setBusy(false) }
  }

  const urlWebhook = typeof window !== 'undefined'
    ? `${window.location.origin}${API_ROUTES.whatsapp.webhook}`
    : ''

  if (loading) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
        <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      </div>
    )
  }

  return (
    <div>
      <header style={{ marginBottom: 16 }}>
        <h1 className="page-title">WhatsApp</h1>
        <p className="page-subtitle" style={{ margin: 0 }}>Conexão, chave-mestra de envio e templates.</p>
      </header>

      {!souTitular && (
        <div className="badge badge-warn" style={{ marginBottom: 12, display: 'block', padding: '8px 12px' }}>
          Só o médico titular altera a conexão. Você pode consultar.
        </div>
      )}

      {/* chave-mestra */}
      <section className="card" style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <ShieldAlert size={18} aria-hidden style={{ color: cfg.whatsapp_ativo ? 'var(--ok)' : 'var(--danger)' }} />
          <strong style={{ fontSize: 15 }}>Chave-mestra de envio</strong>
        </div>
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: '0 0 10px' }}>
          Desligar aqui bloqueia <strong>todo</strong> envio de WhatsApp deste workspace — inclusive automações.
          O bloqueio vale no banco, não só na tela.
        </p>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 8 }}>
          <input type="checkbox" checked={cfg.whatsapp_ativo} disabled={!souTitular}
            onChange={e => salvarConfig({ whatsapp_ativo: e.target.checked })} style={{ width: 18, height: 18 }} />
          Envio de WhatsApp ativo
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 8 }}>
          <input type="checkbox" checked={cfg.envio_automatico} disabled={!souTitular}
            onChange={e => salvarConfig({ envio_automatico: e.target.checked })} style={{ width: 18, height: 18 }} />
          Permitir disparos automáticos (lembretes/régua) — desligado por padrão
        </label>
        <label style={{ fontSize: 14, display: 'flex', gap: 8, alignItems: 'center' }}>
          Teto de envios automáticos por hora:
          <input
            type="number" min={1} max={500} className="field-input" style={{ width: 100, minHeight: 34 }}
            value={cfg.teto_envios_hora} disabled={!souTitular}
            onChange={e => salvarConfig({ teto_envios_hora: Number(e.target.value) })}
          />
        </label>
      </section>

      {/* conexão */}
      <form onSubmit={salvarConexao} className="card" style={{ padding: 18, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <MessageCircle size={18} aria-hidden style={{ color: 'var(--primary)' }} />
          <strong style={{ fontSize: 15 }}>Conexão</strong>
          {canal && (
            <span className={`badge ${canal.status === 'conectado' ? 'badge-ok' : canal.status === 'erro' ? 'badge-danger' : 'badge-muted'}`}>
              {canal.status}
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div>
            <label className="field-label" htmlFor="wa-prov">Provedor</label>
            <select id="wa-prov" className="field-input" value={provedor} disabled={!souTitular}
              onChange={e => setProvedor(e.target.value as typeof provedor)}>
              <option value="simulado">Simulado (testes, sem custo)</option>
              <option value="cloud_api">WhatsApp Cloud API (oficial Meta)</option>
            </select>
          </div>
          <div>
            <label className="field-label" htmlFor="wa-nome">Nome do canal</label>
            <input id="wa-nome" className="field-input" value={nome} disabled={!souTitular} onChange={e => setNome(e.target.value)} />
          </div>
          <div>
            <label className="field-label" htmlFor="wa-tel">Telefone exibido</label>
            <input id="wa-tel" className="field-input" value={telefone} disabled={!souTitular} onChange={e => setTelefone(e.target.value)} placeholder="(48) 3000-0000" />
          </div>
        </div>

        {provedor === 'cloud_api' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div>
              <label className="field-label" htmlFor="wa-pni">Phone Number ID</label>
              <input id="wa-pni" className="field-input" value={phoneNumberId} disabled={!souTitular} onChange={e => setPhoneNumberId(e.target.value)} placeholder="ID do número na Meta" />
            </div>
            <div>
              <label className="field-label" htmlFor="wa-vt">Verify token do webhook</label>
              <input id="wa-vt" className="field-input" value={verifyToken} disabled={!souTitular} onChange={e => setVerifyToken(e.target.value)} />
            </div>
          </div>
        )}

        {erro && <div className="field-error" role="alert">{erro}</div>}
        {aviso && <div className="badge badge-ok" role="status" style={{ padding: '8px 12px' }}>{aviso}</div>}

        {souTitular && (
          <div>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
              Salvar conexão
            </button>
          </div>
        )}

        {provedor === 'cloud_api' && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, fontSize: 13.5, color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text)' }}>Para ativar o número oficial:</strong>
            <ol style={{ margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.7 }}>
              <li>No painel da Meta, aponte o webhook para:
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                  <code style={{ fontSize: 12, background: 'var(--bg)', padding: '6px 8px', borderRadius: 6, wordBreak: 'break-all' }}>{urlWebhook}</code>
                  <button type="button" className="btn-secondary" style={{ minHeight: 30, padding: '2px 8px', fontSize: 12 }}
                    onClick={async () => { await navigator.clipboard.writeText(urlWebhook); setCopiado(true); setTimeout(() => setCopiado(false), 2000) }}>
                    {copiado ? <Check size={12} aria-hidden /> : <Copy size={12} aria-hidden />}
                  </button>
                </div>
              </li>
              <li>Use o <em>verify token</em> acima na verificação e assine o campo <code>messages</code>.</li>
              <li>Grave o <strong>access token</strong> e o <strong>app secret</strong> pelo comando abaixo (eles nunca passam pelo navegador):</li>
            </ol>
            <pre style={{ background: 'var(--bg)', padding: 10, borderRadius: 8, fontSize: 11.5, overflowX: 'auto', marginTop: 8 }}>
{`-- No SQL Editor do Supabase (canal_credenciais não é legível pelo app):
insert into canal_credenciais (canal_conexao_id, access_token, app_secret)
values ('${canal?.id ?? '<id-da-conexao>'}', 'SEU_ACCESS_TOKEN', 'SEU_APP_SECRET')
on conflict (canal_conexao_id) do update
  set access_token = excluded.access_token,
      app_secret   = excluded.app_secret,
      atualizado_em = now();`}
            </pre>
            <p style={{ margin: '8px 0 0' }}>
              O servidor também precisa de <code>SUPABASE_SERVICE_ROLE</code> no <code>.env</code> para enviar e receber.
            </p>
          </div>
        )}
      </form>

      {/* templates */}
      <section className="card" style={{ padding: 18 }}>
        <strong style={{ fontSize: 15, display: 'block', marginBottom: 4 }}>Templates</strong>
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
          Fora da janela de 24h, o WhatsApp só entrega mensagem por template aprovado. Use <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>… para as variáveis.
        </p>

        {templates.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {templates.map(t => (
              <div key={t.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{t.nome}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{t.corpo}</div>
                </div>
                <span className={`badge ${t.status === 'APPROVED' ? 'badge-ok' : t.status === 'REJECTED' ? 'badge-danger' : 'badge-warn'}`}>{t.status}</span>
                {souTitular && t.status !== 'APPROVED' && (
                  <button
                    className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }}
                    onClick={async () => { if (!current) return; await aprovarTemplateLocal(current.workspace.id, t.id); await carregar() }}
                  >
                    {provedor === 'simulado' ? 'Aprovar (simulado)' : 'Marcar aprovado'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {souTitular && (
          <form onSubmit={novoTemplate} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
              <input className="field-input" placeholder="nome_do_template" value={tplNome} onChange={e => setTplNome(e.target.value)} aria-label="Nome do template" />
            </div>
            <textarea
              className="field-input" rows={2} style={{ resize: 'vertical' }}
              placeholder="Olá {{1}}, sua consulta está marcada para {{2}}. Podemos confirmar?"
              value={tplCorpo} onChange={e => setTplCorpo(e.target.value)} aria-label="Corpo do template"
            />
            <div>
              <button type="submit" className="btn-primary" disabled={busy}>
                <Plus size={15} aria-hidden /> Criar template
              </button>
            </div>
          </form>
        )}
      </section>

      <div style={{ marginTop: 14 }}>
        <Link to="/atendimento" style={{ fontSize: 13.5, color: 'var(--primary-deep)', fontWeight: 600 }}>← Ir para o Atendimento</Link>
      </div>
    </div>
  )
}
